import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { CircularProgress } from '@mui/material';

import { type InquiryForm as FormType } from '../../../models/Inquiry/Inquiry';
import { postForm } from '../../../services/inquiry';

import FormDefinition from './FormDefinition';

import classes from '../inquiries.module.css';
import './InquiryForm.css';

interface Props {
  isLoading?: boolean
  data?: FormType
  formID?: number
}

export default function InquiryForm({ isLoading, data, formID }: Props) {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [currentTab] = useState<'webdb' | 'oshirase'>('oshirase');

  const defaultValues: FormType = data ? {
    ...data,
    mail_template_id: Number(data.mail_template_id) || 0,
    smtp_id: Number(data.smtp_id) || 0,
  } : {
    name: '',
    description: '',
    captcha: false,
    confirm_page: false,
    definition: [],
    receiving_mail_address: '',
    list_layout: [[], []],
    details_layout: [[], []],
    form_type: 1,
    mail_template_id: 0,
    smtp_id: 0,
  };

  const {
    register,
    control,
    handleSubmit,
    getValues,
  } = useForm<FormType>({
    defaultValues,
  });

  const submitHandler = async (formData: FormType): Promise<void> => {
    if (!formData.definition || formData.definition.length === 0) {
      console.warn('Please select items');
      return;
    }

    if (formID) formData.id = formID;

    setIsSaving(true);
    await postForm(formData)
      .then((res) => {
        if (res?.data?.success) {
          navigate('/inquiry-management');
        }
      }).catch((error) => {
        console.error('Failed to register table', error);
      }).finally(() => {
        setIsSaving(false);
      });
  };

  const navigateBack = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/inquiry-management');
  };

  return (
    <main className={classes.main}>
      <div className="container-fluid p-3">
        <div className="d-flex align-items-center mb-3">
          <div className="flex-fill">
            <Link to="/inquiry-management" className="text-dark me-3">
              <i className="fa-solid fa-chevron-left fa-fw" />
              Back to list
            </Link>
            <h3 className="h3 d-inline align-middle">{formID ? 'Edit inquiry' : 'Register inquiry'}</h3>
          </div>
        </div>

        <form
          onSubmit={handleSubmit(submitHandler)}
          id="item_form"
          className="card"
          key={data?.id || 'new'}
        >
          <div className="card-body">
            <div className="d-flex flex-column gap-3">
              <div className="row">
                <div className="col-sm-2">
                  <div className="d-flex align-items-center gap-2">
                    <label className="col-form-label fw-bold">Name</label>
                    <span className="label-required">Required</span>
                  </div>
                </div>
                <div className="col-sm-10">
                  <input
                    {...register('name')}
                    type="text"
                    className="form-control"
                    placeholder="Enter name"
                    disabled={isLoading}
                    required
                  />
                  <div className="invalid-feedback">Please enter a name.</div>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-2">
                  <label className="col-form-label fw-bold">Description</label>
                </div>
                <div className="col-sm-10">
                  <textarea
                    {...register('description')}
                    className="form-control"
                    rows={4}
                    placeholder="Enter description here"
                    disabled={isLoading}
                  />
                </div>
              </div>

            </div>

            {/* Fixed form_type for inquiry */}
            <input type="hidden" {...register('form_type')} value={1} />

            <FormDefinition
              formValues={getValues()}
              getFormValues={getValues}
              control={control}
              register={register}
              disabled={isLoading}
              currentTab={currentTab}
            />
          </div>

          <div className="card-footer border-top bg-white text-center">
            <button type="button" onClick={navigateBack} className="btn btn-secondary">Cancel</button>

            <button
              type="submit"
              disabled={isSaving}
              className="btn btn-primary mx-2"
            >
              <span className="d-flex align-items-center gap-2">
                Save
                {isSaving && !isLoading && (<CircularProgress size={15} />)}
              </span>
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
